import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CATEGORIES, DESKS, deskForCategory } from '../demo/catalog.js';
import { useDemo } from '../demo/DemoContext.jsx';
import { loadTickets, staffName, titleFor, updateTicket } from '../demo/store.js';
import { Avatar, Chip, NewReplyChip, Pri, ServerNotice, SlaText, SourceChip, SourceLegend, StatusChip } from '../components/Chips.jsx';
import { useUnread } from '../lib/useUnread.js';
import { isOpen, ownedBy, slaFor, worstSla } from '../lib/ticketView.js';
import { shortWhen } from '../lib/tone.js';
import UrgentAlerts from '../components/UrgentAlerts.jsx';
import NoticePanel from '../components/NoticePanel.jsx';

/* The IT queue.
 *
 * One list, filters across the top with their counts, and checkboxes for the
 * "same outage, six tickets" morning. Sorted worst-priority-first then
 * oldest-first inside a priority, which is what "what should I do next"
 * actually means.
 */

const PRIORITY_ORDER = { P1: 0, P2: 1, P3: 2, P4: 3 };
const BRANDS = ['Beehive Plumbing', 'Superior', 'Master Rooter', 'Comfort Solutions', 'Walker', 'Intermountain Home Services'];

export default function Queue() {
  const { reference, version, me, meStaffId, myDesks, isAdmin, walkthrough } = useDemo();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState(null);
  const [serverState, setServerState] = useState('ok');
  const [hiddenDemo, setHiddenDemo] = useState([]);
  const [view, setView] = useState('open');
  const [desk, setDesk] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const unread = useUnread();

  useEffect(() => {
    let cancelled = false;
    loadTickets().then((r) => {
      if (cancelled) return;
      setTickets(r.rows);
      setServerState(r.serverState);
      setHiddenDemo(r.hiddenDemo);
    });
    return () => {
      cancelled = true;
    };
  }, [version]);

  /* THE DESK WALL. A ticket belongs to the desk that owns its category, and
   * you only ever see the desks you work. Rhea's team opening the queue sees
   * ServiceTitan tickets, not 200 broken laptops.
   *
   * Derived from the category, never stored on the ticket: re-pointing a
   * category at a different desk should move its existing tickets too, which
   * is what you want when a team's scope changes.
   *
   * Normal builds derive myDesks from the server-resolved Hub rooms.
   * The API enforces the same boundary before reading rows. */
  const visible = useMemo(
    () => (tickets || []).filter((t) => (isAdmin || myDesks.includes(deskForCategory(t.categoryId)?.id))),
    [tickets, myDesks, isAdmin]
  );

  const all = visible;

  const atRisk = useMemo(
    () =>
      all.filter((t) => {
        if (!isOpen(t) || t.status === 'waiting') return false;
        const w = worstSla(slaFor(t, reference));
        return w && (w.state === 'at_risk' || w.state === 'breached');
      }),
    [all, reference]
  );

  // Counts live on the tabs. "Breaching 2" is the number you want to see
  // without clicking, and a zero is worth seeing too.
  const VIEWS = [
    { id: 'open', label: 'All open', rows: all.filter(isOpen) },
    { id: 'mine', label: 'Mine', rows: all.filter((t) => isOpen(t) && ownedBy(t, { meStaffId, email: me.email })) },
    { id: 'unassigned', label: 'Unassigned', rows: all.filter((t) => isOpen(t) && !t.assignee) },
    { id: 'breaching', label: 'Breaching', rows: atRisk },
    { id: 'waiting', label: 'Waiting', rows: all.filter((t) => t.status === 'waiting') },
    { id: 'all', label: 'Everything', rows: all },
  ];

  const visibleCategories = CATEGORIES.filter((c) =>
    desk ? c.deskId === desk : myDesks.includes(c.deskId)
  );

  const rows = useMemo(() => {
    let out = VIEWS.find((v) => v.id === view)?.rows || [];
    if (desk) out = out.filter((t) => deskForCategory(t.categoryId)?.id === desk);
    if (category) out = out.filter((t) => t.categoryId === category);
    if (brand) out = out.filter((t) => t.requester.brand === brand);
    // Worst priority first, then oldest — except that a ticket on hold sinks
    // below the live ones at the same priority. A paused P1 is not the next
    // thing anyone should do, and leaving it on top makes the whole list lie.
    return [...out].sort(
      (a, b) =>
        PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority] ||
        (a.status === 'waiting') - (b.status === 'waiting') ||
        new Date(a.raisedAt) - new Date(b.raisedAt)
    );
  }, [visible, view, desk, category, brand, reference, meStaffId]);

  function toggle(number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(number)) next.delete(number);
      else next.add(number);
      return next;
    });
  }

  /* A filed ticket has no server command for assign or priority yet, so a bulk
   * action must skip it - and SAY so (see the bulk bar) - rather than edit a
   * browser copy that the server never hears about. */
  // Decided by what the ROW is, not by its number: a demo and a real ticket can share one.
  const demoNumbers = new Set((tickets || []).filter((t) => !t.fromServer).map((t) => t.number));
  const bulkable = [...selected].filter((n) => demoNumbers.has(n));
  const skipped = selected.size - bulkable.length;

  async function bulkAssignToMe() {
    for (const number of bulkable) {
      await updateTicket(number, { assignee: meStaffId }, { by: me.name, source: 'demo' });
    }
    setSelected(new Set());
  }

  async function bulkPriority(priority) {
    for (const number of bulkable) {
      await updateTicket(
        number,
        { priority, overrideReason: 'Set in bulk from the queue' },
        { by: me.name, source: 'demo' }
      );
    }
    setSelected(new Set());
  }

  return (
    <>
      <section className="hero" style={{ paddingBottom: 0 }}>
        <h1 className="hero__title" style={{ fontSize: 34 }}>
          The <strong>queue</strong>
        </h1>
        <p className="hero__sub">
          {/* Bulk actions change only demo tickets, so the live app does not offer them (#122 review D1). */}
          {walkthrough
            ? 'Worst first, then oldest. Tick a few and act on them together — six reports of one outage should cost one action, not six.'
            : 'Worst first, then oldest.'}
        </p>
      </section>

      <UrgentAlerts />

      <NoticePanel />

      <div className="qwin">
        <div className="qtools">
          {VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`qtab${view === v.id ? ' qtab--on' : ''}`}
              onClick={() => {
                setView(v.id);
                setSelected(new Set());
              }}
            >
              {v.label}
              <b>{v.rows.length}</b>
            </button>
          ))}

          <div className="qtools__right">
            <label className="qf">
              <span className="material-symbols-outlined" aria-hidden="true">filter_list</span>
              <select value={brand} onChange={(e) => setBrand(e.target.value)} aria-label="Brand">
                <option value="">Every brand</option>
                {BRANDS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </label>
            {/* The desk filter only appears if you actually work more than
                one. For Rhea, who works only ServiceTitan, a dropdown with a
                single option would be a control that does nothing. */}
            {myDesks.length > 1 ? (
              <label className="qf">
                <span className="material-symbols-outlined" aria-hidden="true">support_agent</span>
                <select
                  value={desk}
                  onChange={(e) => {
                    setDesk(e.target.value);
                    // The category filter is scoped to the desk, so a leftover
                    // category from the previous desk would show an empty list
                    // and look like a bug.
                    setCategory('');
                  }}
                  aria-label="Desk"
                >
                  <option value="">Every desk</option>
                  {DESKS.filter((d) => myDesks.includes(d.id)).map((d) => (
                    <option key={d.id} value={d.id}>{d.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {/* Only categories you can actually see tickets for. Offering
                "Phones" to someone who works only the ServiceTitan desk would
                filter a list that can never contain one — and where that
                leaves a single option, the whole control is hidden. A choice
                of one is not a choice. */}
            {visibleCategories.length > 1 ? (
              <label className="qf">
                <span className="material-symbols-outlined" aria-hidden="true">category</span>
                <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
                  <option value="">Every category</option>
                  {visibleCategories.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </label>
            ) : null}
            <span className="qf">
              <span className="material-symbols-outlined" aria-hidden="true">sort</span>
              Priority, then age
            </span>
          </div>
        </div>

        <ServerNotice state={serverState} />
        <SourceLegend rows={tickets} hiddenDemo={hiddenDemo} />
        <div className="qhead">
          <div />
          <div>Pri</div>
          <div>Ticket</div>
          <div>Requester</div>
          <div>Status</div>
          <div>Owner</div>
          <div>Response</div>
        </div>

        {tickets === null ? (
          <p className="muted" style={{ padding: 20 }}>Loading…</p>
        ) : rows.length === 0 ? (
          <div style={{ padding: 20 }}>
            <div className="empty">
              <p className="empty__title">Nothing here</p>
              <p className="empty__body">
                {view === 'breaching'
                  ? 'Nothing is at risk or over its target. That is the state you want this view in.'
                  : 'No tickets match this view.'}
              </p>
            </div>
          </div>
        ) : (
          rows.map((t) => {
            const sla = slaFor(t, reference);
            const worst = worstSla(sla);
            const owner = staffName(t.assignee);
            const isSel = selected.has(t.number);
            return (
              <div key={t.number} className={`qrow${isSel ? ' qrow--sel' : ''}`}>
                {walkthrough ? (
                <button
                  type="button"
                  className={`chk${isSel ? ' chk--on' : ''}`}
                  aria-label={`Select ${t.number}`}
                  aria-pressed={isSel}
                  onClick={() => toggle(t.number)}
                >
                  {isSel ? <span className="material-symbols-outlined" aria-hidden="true">check</span> : null}
                </button>
                ) : <span />}

                <div>
                  <Pri priority={t.priority} ruleName={t.priorityRuleName} />
                </div>

                <button
                  type="button"
                  className="qcell"
                  style={cellBtn}
                  onClick={() => navigate(`/ticket/${t.number}`)}
                >
                  <b>{titleFor(t)}</b>
                  <small>
                    <span className="ref">{t.number}</span> <SourceChip ticket={t} /> <NewReplyChip ticket={t} unread={unread} />
                    {t.similarCount > 1 ? ` · ${t.similarCount} reports` : ''}
                    {' · raised '}
                    {shortWhen(t.raisedAt)}
                  </small>
                </button>

                <div className="qcell">
                  <b>{t.requester.name}</b>
                  <small>{t.requester.role} · {t.requester.brand}</small>
                </div>

                <div>
                  <StatusChip ticket={t} />
                </div>

                <div className="qwho">
                  <Avatar name={owner} kind="it" small />
                  <span>{owner ? owner.split(' ')[0] : ''}</span>
                </div>

                <div>
                  <SlaText standing={worst} paused={t.status === 'waiting'} />
                </div>
              </div>
            );
          })
        )}

        {walkthrough && selected.size ? (
          <div className="bulk">
            <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: 16 }}>check_circle</span>
            <b>{selected.size} selected</b>
            {skipped ? (
              <span className="muted" role="status" style={{ fontSize: 11.5 }}>
                {skipped} filed {skipped === 1 ? 'ticket' : 'tickets'} can't be changed in bulk yet
                and will be left alone.
              </span>
            ) : null}
            <button type="button" className="bulk__btn" onClick={bulkAssignToMe} disabled={!bulkable.length}>Assign to me</button>
            {['P1', 'P2', 'P3', 'P4'].map((p) => (
              <button key={p} type="button" className="bulk__btn" disabled={!bulkable.length} onClick={() => bulkPriority(p)}>
                Set {p}
              </button>
            ))}
            <button
              type="button"
              className="bulk__btn"
              onClick={() =>
                window.alert(
                  'Merging duplicates is not in this prototype.\n\nIt is a real question for the walkthrough: when six people report one outage, does the merged ticket keep all six requesters on the thread, or does it close five with a message pointing at the sixth?'
                )
              }
            >
              Merge as duplicates
            </button>
            <button type="button" className="bulk__btn" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        ) : null}
      </div>

      {walkthrough ? (
        <p className="muted" style={{ marginTop: 12, fontSize: 11.5 }}>
          <Chip tone="flat" icon="science">Prototype</Chip>{' '}
          Bulk assign and bulk priority really change the tickets. Merge is deliberately left
          undone — how it should behave is worth deciding out loud first.
        </p>
      ) : null}
    </>
  );
}

const cellBtn = {
  border: 'none',
  background: 'none',
  padding: 0,
  textAlign: 'left',
  cursor: 'pointer',
  minWidth: 0,
  font: 'inherit',
  color: 'inherit',
};
